<script lang="ts" context="module">
  // import { type EventHandler } from "svelte/elements";

  // We add a non-standard event handler to the document. TypeScript does not
  // like that, so we have to extend the list of DOM attributes of svelte by
  // adding the event handler.
  declare module 'svelte/elements' {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    export interface DOMAttributes<T extends EventTarget> {
      // Preferably, we would not use `any` but the right type. However, then we
      // have to import the EventHandler type from svelte, which esbuild
      // complains about. Since we only need the non-standard attribute in this
      // file, we just do the minimum needed to make it work.
      // eslint-disable-next-line
      'on:webkitfullscreenchange'?: any; //EventHandler<Event, T> | undefined | null;
    }
  }

  interface WebkitHTMLVideoElement extends HTMLVideoElement {
    webkitDisplayingFullscreen: boolean;
    webkitSupportsFullscreen: boolean;
    webkitExitFullscreen(): void;
    webkitEnterFullscreen(): void;
  }
</script>

<script lang="ts">
  import { PlayerEvent, type PlayerInterface } from '@foundation-player/loader';
  import { getContext, onDestroy, onMount } from 'svelte';
  import { get } from 'svelte/store';
  import FullscreenFill from '../../shared/assets/icons/FullscreenFill.svelte';
  import FullscreenReduce from '../../shared/assets/icons/FullscreenReduce.svelte';
  import IconButton from '../../shared/components/IconButton.svelte';
  import type { FocusItem, FocusLayer } from '../../shared/focus-manager/focus-types';
  import type { UIStateContext } from '../types/internal/ui-state';
  import type { UIConfig } from '../types/ui-config';
  import { UIEvent } from '../types/ui-event';

  /** The element which should be set to fullscreen */
  export let fullscreenElement: HTMLElement;
  export let player: PlayerInterface;
  export let focusLayer: FocusLayer;
  export let focusName: FocusItem;

  const { allowNativeVideoFullscreen = false, fullscreenEventsOnly, experimental } = getContext<UIConfig>('uiConfig');

  const uiState = getContext<UIStateContext>('uiState');

  let fullscreenSupported = false;
  let displayingFullscreen = false;
  let videoElement: HTMLVideoElement | WebkitHTMLVideoElement | undefined = player.getVideoElement();

  // We define a common interface for the various ways to handle fullscreen:
  interface FullscreenAPI {
    enterFullscreen(): Promise<void>;
    exitFullscreen(): Promise<void>;
    fullscreenEnabled: boolean;
    inFullscreen: boolean;
  }

  // This is how fullscreen works on the Video element in webkit (i.e. Safari on
  // iPhone):
  const webkitVideoFullscreenAPI: FullscreenAPI = {
    enterFullscreen() {
      if (videoElement) {
        (videoElement as WebkitHTMLVideoElement).webkitEnterFullscreen();
      }

      return Promise.resolve();
    },
    exitFullscreen() {
      if (videoElement) {
        (videoElement as WebkitHTMLVideoElement).webkitExitFullscreen();
      }

      return Promise.resolve();
    },
    get fullscreenEnabled() {
      // Should be:
      // return videoElement ? (videoElement as WebkitHTMLVideoElement).webkitSupportsFullscreen : false;
      // but on mount, Safari sometimes has webkitSupportsFullscreen set to
      // false (probably waiting for user interaction?)
      // So we use the presence of the function for detection instead.
      return videoElement ? 'webkitEnterFullscreen' in videoElement : false;
    },
    get inFullscreen() {
      return videoElement ? (videoElement as WebkitHTMLVideoElement).webkitDisplayingFullscreen : false;
    },
  };

  // This is how fullscreen works as defined in HTML5 on any element:
  const html5FullscreenAPI: FullscreenAPI = {
    enterFullscreen() {
      return fullscreenElement.requestFullscreen();
    },
    exitFullscreen() {
      return document.exitFullscreen();
    },
    get fullscreenEnabled() {
      return document.fullscreenEnabled;
    },
    get inFullscreen() {
      // we do NOT have to check if our fullscreen element actually is the
      // element being displayed in fullscreen, as one couldn't see our player
      // with the wrong button anyway when another element is in fullscreen
      return document.fullscreenElement != null;
    },
  };

  // This is how we handle the experimental feature to just emit events and let
  // the integrator mimic a fullscreen mode on the mount-point:
  const eventsOnlyFullscreenAPI: FullscreenAPI = {
    enterFullscreen() {
      player.dispatchEvent(new Event(UIEvent.EnterFullscreenRequest));
      uiState.fullscreenEventsOnlyInFullscreen.set(true);
      return Promise.resolve();
    },
    exitFullscreen() {
      player.dispatchEvent(new Event(UIEvent.ExitFullscreenRequest));
      uiState.fullscreenEventsOnlyInFullscreen.set(false);
      return Promise.resolve();
    },
    get inFullscreen() {
      return get(uiState.fullscreenEventsOnlyInFullscreen);
    },
    fullscreenEnabled: fullscreenEventsOnly ?? experimental?.fullscreenEventsOnly ?? false,
  };

  // We prefer the HTML5 fullscreen API, but if that's not available, we fall
  // back to the native video fullscreen API if available and allowed in the UI
  // config. If both fail, we try the emit-event fullscreen API, which allows
  // the integrator to mimic a fullscreen, for example, by increasing the size
  // of the mount point to cover the whole viewport.
  const fullscreenAPI: FullscreenAPI | undefined = html5FullscreenAPI.fullscreenEnabled
    ? html5FullscreenAPI
    : allowNativeVideoFullscreen && webkitVideoFullscreenAPI.fullscreenEnabled
      ? webkitVideoFullscreenAPI
      : eventsOnlyFullscreenAPI.fullscreenEnabled
        ? eventsOnlyFullscreenAPI
        : undefined;

  const exitFullscreenOnSessionEnd = () => {
    if (!fullscreenAPI) {
      return;
    }

    if (fullscreenAPI.inFullscreen) {
      fullscreenAPI.exitFullscreen();
      fullscreenChanged();
    }
  };

  onMount(() => {
    if (!fullscreenAPI) {
      return;
    }

    fullscreenSupported = fullscreenAPI.fullscreenEnabled;

    if (fullscreenSupported) {
      displayingFullscreen = fullscreenAPI.inFullscreen;

      // Exit fullscreen mode at end of session (content + post-rolls)
      player.addEventListener(PlayerEvent.SessionEnd, exitFullscreenOnSessionEnd);
    }
  });

  onDestroy(() => {
    if (fullscreenAPI) {
      player.removeEventListener(PlayerEvent.SessionEnd, exitFullscreenOnSessionEnd);
    }
  });

  const toggleFullscreen = async () => {
    if (!fullscreenAPI) {
      return;
    }

    if (fullscreenAPI.inFullscreen) {
      await fullscreenAPI.exitFullscreen();
    } else {
      await fullscreenAPI.enterFullscreen();
    }

    fullscreenChanged();
  };

  function fullscreenChanged() {
    if (!fullscreenAPI) {
      return;
    }

    displayingFullscreen = fullscreenAPI.inFullscreen;
  }
</script>

<svelte:document
  on:fullscreenchange={fullscreenSupported ? fullscreenChanged : undefined}
  on:webkitfullscreenchange={fullscreenSupported ? fullscreenChanged : undefined}
/>

{#if fullscreenSupported}
  <IconButton
    on:click={toggleFullscreen}
    title={displayingFullscreen ? 'Vollbildmodus beenden' : 'Vollbild'}
    {focusLayer}
    {focusName}
  >
    {#if displayingFullscreen}
      <FullscreenReduce aria-hidden={true} />
    {:else}
      <FullscreenFill aria-hidden={true} />
    {/if}
  </IconButton>
{/if}
